import React, { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Loader2, ShieldCheck, Eye, EyeOff, Check } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Logo from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

// Front door — everything the customer needs on ONE page:
// full-bleed athlete photo left, single Sign-In-or-Create-Account
// card right with a tab switch. No wall of text. Yellow used sparingly
// on the CTA + one accent line. Everything else is high-contrast white
// on dark so it reads as premium, not busy.
const HERO = "/marketing/hero_trail_runner.jpg";

// Packages shown inline on the Create-account tab so athletes can see and
// choose what they're paying for before signing up. Prices mirror /subscribe.
const PACKAGES = [
  {
    lookup: "spc_monthly", name: "Full Plan", price: "£14.99", unit: "/ month",
    badge: "7 days free", blurb: "Weekly fuelling + unlimited race plans. Cancel anytime.",
    features: [
      "Daily calorie, carb, protein and fat targets from your real training load",
      "Personalised weekly meal plans + shopping list",
      "Unlimited race plans — carb load, race-morning meal, in-race timetable",
      "Batch cook sheet with real grams per meal",
      "Weight tracking, progress photos and weekly recap emails",
      "AI coach chat for any fuelling question",
    ],
  },
  {
    lookup: "race_pack_oneoff", name: "Race Pack", price: "£9.99", unit: "one-off",
    blurb: "One race only. No subscription, nothing to cancel.",
    features: [
      "Race week carb-load timeline (T-3 → T+1)",
      "Race-morning meal timed to your gun time",
      "20-minute in-race fuelling timetable with carbs/hr and sodium",
      "What to carry + kit checklist, downloadable as a PDF",
      "Credit valid 12 months",
    ],
  },
  {
    lookup: "race_pack_annual", name: "Race Season", price: "£19.99", unit: "one-off",
    blurb: "Every race you enter for 12 months. No subscription.",
    features: [
      "Everything in Race Pack, for unlimited races",
      "Valid for 12 months from purchase",
      "Ideal if you've got a full race calendar",
    ],
  },
];

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // Only show the Create-Account form if the athlete has already picked a plan
  // on /subscribe (pending selection in sessionStorage). Otherwise send them
  // to /subscribe first so they choose what they're paying for BEFORE creating
  // an account.
  const pendingRaw = typeof window !== "undefined" ? sessionStorage.getItem("spc_pending_plan") : null;
  let pendingPlan = null;
  try { pendingPlan = pendingRaw ? JSON.parse(pendingRaw) : null; } catch {}
  const initialMode = location.state?.mode === "register" ? "register" : "signin";
  const [mode, setMode] = useState(initialMode);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  // Packages are chosen right here, before signing up — no trip to /subscribe.
  const [picked, setPicked] = useState(pendingPlan?.lookup_key || "spc_monthly");
  const [addTracker, setAddTracker] = useState(pendingPlan?.add_tracker ?? true);
  const [openDetails, setOpenDetails] = useState(null);

  const goCreateAccount = () => setMode("register");

  const isRegister = mode === "register";

  const submit = async (e) => {
    e.preventDefault();
    if (isRegister && !name.trim()) { toast.error("Enter your name"); return; }
    if (!email || !password) { toast.error("Enter your email and password"); return; }
    if (isRegister && password.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    setLoading(true);
    try {
      if (isRegister) {
        // Persist the package picked above so the post-register redirect sends
        // them straight to the right Stripe checkout.
        try {
          sessionStorage.setItem("spc_pending_plan", JSON.stringify(
            picked === "spc_monthly"
              ? { lookup_key: picked, add_tracker: addTracker, add_tp_premium: false, tp_email: null }
              : { lookup_key: picked }));
        } catch {}
        const { data } = await api.post("/auth/register", { name: name.trim(), email: email.trim(), password });
        login(data.token, data.user);
        // If the athlete had already picked a plan on /subscribe, jump straight
        // to Stripe using their saved selection — no need to re-tick anything.
        const pendingRaw = sessionStorage.getItem("spc_pending_plan");
        if (pendingRaw) {
          try {
            const pending = JSON.parse(pendingRaw);
            sessionStorage.removeItem("spc_pending_plan");
            const { data: co } = await api.post("/payments/checkout", {
              lookup_key: "spc_monthly",
              add_tracker: !!pending.add_tracker,
              add_tp_premium: !!pending.add_tp_premium,
              tp_email: pending.add_tp_premium ? (pending.tp_email || "") : null,
              origin_url: window.location.origin,
            });
            if (co?.checkout_url) { window.location.href = co.checkout_url; return; }
            toast.error(co?.error || "Could not start checkout — please retry from your plan page.");
            navigate("/subscribe");
            return;
          } catch { sessionStorage.removeItem("spc_pending_plan"); }
        }
        toast.success("Welcome — let's pick your plan");
        navigate("/subscribe");
      } else {
        const { data } = await api.post("/auth/login", { email: email.trim(), password });
        login(data.token, data.user);
        toast.success("Signed in");
        navigate(data.user.role === "admin" ? "/admin" : "/");
      }
    } catch (err) {
      toast.error(apiError(err, isRegister ? "Could not create your account." : "Sign in failed. Please check your details."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen grid lg:grid-cols-[1.1fr_1fr] bg-[#0A0A0A] text-white font-sans overflow-hidden">
      {/* Full-width brand banner — spans both hero and auth columns so the
          wordmark reads cleanly against any background. Beta feedback:
          "black across the top is different to that on right, logo and
          name need to be bigger". */}
      <div className="absolute top-0 inset-x-0 h-20 sm:h-24 bg-black z-30 pointer-events-none lg:col-span-2" />
      <Link to="/" className="absolute top-3 left-4 sm:top-4 sm:left-10 z-40 flex items-center gap-3 sm:gap-4 group">
        <Logo className="h-12 w-12 sm:h-16 sm:w-16" />
        <div className="leading-tight">
          <p className="font-display text-lg sm:text-2xl uppercase tracking-tight text-white">
            <span className="text-[#E2E30A]">Performance</span> Nutrition
          </p>
          <p className="text-[10px] sm:text-xs uppercase tracking-[0.28em] text-zinc-300 mt-0.5 group-hover:text-white transition-colors">Powered by SPC</p>
        </div>
      </Link>

      {/* ─── LEFT · HERO ─────────────────────────────────── */}
      <div className="relative h-[52vh] min-h-[360px] max-h-[560px] lg:h-auto lg:min-h-screen lg:max-h-none overflow-hidden order-first lg:order-first">
        <img src={HERO} alt="Endurance athlete on trail" className="absolute inset-0 w-full h-full object-cover object-center" />
        {/* soft cinematic shroud */}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/40 to-transparent" />
        <div className="hidden lg:block absolute inset-0 bg-gradient-to-r from-transparent via-transparent to-[#0A0A0A]" />
        {/* subtle noise */}
        <div className="absolute inset-0 opacity-[0.04] mix-blend-overlay pointer-events-none"
          style={{ backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='180' height='180'><filter id='n'><feTurbulence baseFrequency='0.9'/></filter><rect width='100%' height='100%' filter='url(%23n)' opacity='0.6'/></svg>\")" }} />

        {/* Bottom-left cinematic quote — minimal copy, big feel */}
        <div className="absolute bottom-4 left-4 right-4 sm:bottom-14 sm:left-10 sm:right-14 z-10">
          <motion.p
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="text-[9px] sm:text-[11px] uppercase tracking-[0.28em] sm:tracking-[0.35em] text-[#E2E30A] font-bold mb-2 sm:mb-4"
          >
            Built by coaches · Tuned to athletes
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 }}
            className="font-display text-3xl sm:text-7xl xl:text-8xl uppercase tracking-tighter font-black leading-[0.9]"
          >
            Fuel every<br /><span className="text-[#E2E30A]">session.</span><br />
            <span className="text-white">Fuel every</span> <span className="text-[#E2E30A]">race.</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.25 }}
            className="hidden sm:block text-zinc-300 mt-5 max-w-md text-sm sm:text-base"
          >
            Personalised weekly fuelling built around your <span className="text-white">real</span> training —
            plus a full <span className="text-white">race week and race day plan</span> for every event you line up for.
          </motion.p>

          {/* Pricing preview — prices must be visible BEFORE signup. */}
          <motion.div
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.38 }}
            className="hidden sm:flex mt-6 flex-wrap items-center gap-2 sm:gap-3"
          >
            <span className="inline-flex items-baseline gap-1.5 border-2 border-[#E2E30A]/70 bg-[#E2E30A]/[0.08] backdrop-blur px-3 py-2">
              <span className="text-[10px] uppercase tracking-widest text-[#E2E30A] font-bold">Full plan</span>
              <span className="font-display text-lg leading-none tracking-tight font-black text-white">£14.99</span>
              <span className="text-[9px] uppercase tracking-widest text-zinc-500">/mo</span>
            </span>
            <span className="text-[10px] uppercase tracking-widest text-zinc-400 font-mono">· 7 days free · cancel anytime</span>
            <span className="inline-flex items-baseline gap-1.5 border border-white/25 bg-black/40 backdrop-blur px-3 py-2">
              <span className="text-[10px] uppercase tracking-widest text-zinc-300 font-bold">Race plan only</span>
              <span className="font-display text-lg leading-none tracking-tight font-black text-white">£9.99</span>
              <span className="text-[9px] uppercase tracking-widest text-zinc-500">one-off</span>
            </span>
            <Link
              to="/subscribe"
              data-testid="login-see-plans"
              className="ml-1 sm:ml-2 inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.22em] text-[#E2E30A] font-bold hover:text-white transition-colors underline-offset-4 hover:underline"
            >
              See what's included →
            </Link>
          </motion.div>
        </div>
      </div>

      {/* ─── RIGHT · AUTH ─────────────────────────────────── */}
      <div className="relative flex items-center justify-center px-5 sm:px-10 py-8 sm:py-12 lg:py-16 bg-[#0A0A0A]">
        {/* soft yellow glow behind card */}
        <div className="absolute inset-0 pointer-events-none opacity-40"
          style={{ background: "radial-gradient(circle at 70% 30%, #E2E30A15 0%, transparent 55%)" }} />

        <motion.div
          key={mode}
          initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}
          className="relative w-full max-w-[420px]"
        >
          {/* Tab pill */}
          <div className="inline-flex bg-white/5 border border-white/10 p-1 mb-6 sm:mb-8" role="tablist">
            <button
              type="button"
              role="tab"
              data-testid="auth-tab-signin"
              aria-selected={!isRegister}
              onClick={() => setMode("signin")}
              className={`px-4 sm:px-5 py-2 text-[11px] uppercase tracking-[0.22em] font-bold transition-colors ${
                !isRegister ? "bg-white text-black" : "text-zinc-400 hover:text-white"
              }`}
            >
              Sign in
            </button>
            <button
              type="button"
              role="tab"
              data-testid="auth-tab-register"
              aria-selected={isRegister}
              onClick={goCreateAccount}
              className={`px-4 sm:px-5 py-2 text-[11px] uppercase tracking-[0.22em] font-bold transition-colors ${
                isRegister ? "bg-[#E2E30A] text-black" : "text-zinc-400 hover:text-white"
              }`}
            >
              Create account
            </button>
          </div>

          <h2 className="font-display text-3xl sm:text-5xl uppercase tracking-tighter font-black leading-[0.95]">
            {isRegister ? <>Pick your <span className="text-[#E2E30A]">package.</span></> : <>Welcome <span className="text-[#E2E30A]">back.</span></>}
          </h2>
          <p className="text-sm text-zinc-400 mt-2 max-w-sm">
            {isRegister ? "Choose what you need, then create your account. £0 today on the monthly plan." : "Sign in to open your fuelling plan and tracker."}
          </p>

          {isRegister && (
            <div className="mt-5 space-y-2" data-testid="plan-picker">
              {PACKAGES.map((p) => {
                const on = picked === p.lookup;
                const showing = openDetails === p.lookup;
                return (
                  <div key={p.lookup} className="border transition-colors"
                    style={{ borderColor: on ? "#E2E30A" : "rgba(255,255,255,0.12)", background: on ? "rgba(226,227,10,0.06)" : "rgba(255,255,255,0.02)" }}>
                    <button type="button" data-testid={`plan-option-${p.lookup}`}
                      onClick={() => setPicked(p.lookup)}
                      className="w-full text-left flex items-start gap-3 p-3.5">
                      <span className="mt-0.5 h-4 w-4 shrink-0 rounded-full border-2 grid place-items-center"
                        style={{ borderColor: on ? "#E2E30A" : "rgba(255,255,255,0.3)" }}>
                        {on && <span className="h-1.5 w-1.5 rounded-full bg-[#E2E30A]" />}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="flex items-baseline gap-2 flex-wrap">
                          <span className="text-[13px] font-bold uppercase tracking-wider text-white">{p.name}</span>
                          {p.badge && <span className="text-[9px] uppercase tracking-widest text-black bg-[#E2E30A] px-1.5 py-0.5 font-bold">{p.badge}</span>}
                        </span>
                        <span className="block text-[11px] text-zinc-400 mt-1 leading-snug">{p.blurb}</span>
                      </span>
                      <span className="text-right shrink-0">
                        <span className="block font-display text-xl leading-none font-black tabular-nums text-white">{p.price}</span>
                        <span className="block text-[9px] uppercase tracking-widest text-zinc-500 mt-1">{p.unit}</span>
                      </span>
                    </button>
                    <button type="button" data-testid={`plan-details-${p.lookup}`}
                      onClick={() => setOpenDetails(showing ? null : p.lookup)}
                      className="w-full text-left px-3.5 pb-2.5 -mt-1 text-[10px] uppercase tracking-[0.18em] text-[#E2E30A] font-bold hover:text-white transition-colors">
                      {showing ? "Hide what you get −" : "What you get +"}
                    </button>
                    <AnimatePresence initial={false}>
                      {showing && (
                        <motion.ul
                          initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.22 }}
                          className="overflow-hidden px-3.5 pb-3.5 space-y-1.5">
                          {p.features.map((f) => (
                            <li key={f} className="flex items-start gap-2 text-[11.5px] text-zinc-200 leading-snug">
                              <Check className="h-3.5 w-3.5 shrink-0 mt-0.5 text-[#E2E30A]" strokeWidth={2.6} />
                              <span>{f}</span>
                            </li>
                          ))}
                        </motion.ul>
                      )}
                    </AnimatePresence>
                  </div>
                );
              })}

              {picked === "spc_monthly" && (
                <button type="button" data-testid="plan-addon-tracker"
                  onClick={() => setAddTracker((v) => !v)}
                  className="w-full text-left flex items-center gap-3 p-3 border transition-colors"
                  style={{ borderColor: addTracker ? "#FF9500" : "rgba(255,255,255,0.12)", background: addTracker ? "rgba(255,149,0,0.06)" : "transparent" }}>
                  <span className="relative h-5 w-9 shrink-0 rounded-full transition-colors"
                    style={{ background: addTracker ? "#FF9500" : "rgba(255,255,255,0.18)" }}>
                    <span className="absolute top-0.5 h-4 w-4 rounded-full bg-white transition-all"
                      style={{ left: addTracker ? "18px" : "2px" }} />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-[11px] font-bold uppercase tracking-wider text-white">Add Food Tracker</span>
                    <span className="block text-[10px] text-zinc-400 mt-0.5">Log meals by search, barcode or photo against your targets</span>
                  </span>
                  <span className="text-[11px] font-bold tabular-nums shrink-0" style={{ color: addTracker ? "#FF9500" : "#71717A" }}>+£4.99</span>
                </button>
              )}

              <div className="flex items-center justify-between gap-2 pt-1">
                <p className="text-[10px] uppercase tracking-wider text-zinc-500">
                  {picked === "spc_monthly"
                    ? <>Total <span className="text-white font-bold">£{((1499 + (addTracker ? 499 : 0)) / 100).toFixed(2)}/mo</span> · £0 today</>
                    : <>One single payment · nothing to cancel</>}
                </p>
                <Link to="/subscribe" data-testid="login-compare-plans"
                  className="text-[10px] uppercase tracking-wider text-[#E2E30A] font-bold hover:text-white transition-colors shrink-0">
                  Compare in full →
                </Link>
              </div>
            </div>
          )}

          {/* Sign-in tab: packages are still visible at a glance — tapping one
              jumps to Create account with it pre-selected. */}
          {!isRegister && (
            <div className="mt-5 space-y-1.5" data-testid="plan-preview">
              <p className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-bold">New here? Our packages</p>
              {PACKAGES.map((p) => (
                <button key={p.lookup} type="button" data-testid={`plan-preview-${p.lookup}`}
                  onClick={() => { setPicked(p.lookup); setMode("register"); }}
                  className="w-full flex items-center gap-2 border border-white/12 bg-white/[0.02] hover:border-[#E2E30A]/60 hover:bg-[#E2E30A]/[0.05] transition-colors px-3 py-2.5 text-left">
                  <span className="text-[11px] font-bold uppercase tracking-wider text-white">{p.name}</span>
                  {p.badge && <span className="text-[8px] uppercase tracking-widest text-black bg-[#E2E30A] px-1.5 py-0.5 font-bold">{p.badge}</span>}
                  <span className="ml-auto font-display text-base leading-none font-black tabular-nums text-white">{p.price}</span>
                  <span className="text-[8px] uppercase tracking-widest text-zinc-500">{p.unit}</span>
                  <ArrowRight className="h-3 w-3 text-[#E2E30A] shrink-0" />
                </button>
              ))}
              <Link to="/subscribe" data-testid="login-see-plans-mobile"
                className="inline-block pt-1 text-[10px] uppercase tracking-[0.2em] text-[#E2E30A] font-bold hover:text-white transition-colors">
                See what's included →
              </Link>
            </div>
          )}

          <form onSubmit={submit} className="mt-6 sm:mt-8 space-y-4">
            <AnimatePresence initial={false} mode="wait">
              {isRegister && (
                <motion.div
                  key="name-field"
                  initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.25 }}
                >
                  <label className="text-[10px] uppercase tracking-[0.22em] text-zinc-400 font-bold">Full name</label>
                  <Input
                    data-testid="auth-name-input"
                    autoFocus={isRegister}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Jordan Ellis"
                    className="mt-1.5 h-12 rounded-none bg-white/5 border-white/12 focus-visible:ring-[#E2E30A] focus-visible:border-[#E2E30A] text-white placeholder:text-zinc-500"
                  />
                </motion.div>
              )}
            </AnimatePresence>

            <div>
              <label className="text-[10px] uppercase tracking-[0.22em] text-zinc-400 font-bold">Email</label>
              <Input
                data-testid={isRegister ? "auth-email-input-register" : "auth-email-input-signin"}
                type="email"
                autoFocus={!isRegister}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@endurance.com"
                className="mt-1.5 h-12 rounded-none bg-white/5 border-white/12 focus-visible:ring-[#E2E30A] focus-visible:border-[#E2E30A] text-white placeholder:text-zinc-500"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-[10px] uppercase tracking-[0.22em] text-zinc-400 font-bold">Password</label>
                {!isRegister && (
                  <Link to="/forgot-password" data-testid="forgot-password-link"
                    className="text-[10px] uppercase tracking-wider text-[#E2E30A] font-bold hover:text-white transition-colors">
                    Forgot password?
                  </Link>
                )}
              </div>
              <div className="relative">
                <Input
                  data-testid={isRegister ? "auth-password-input-register" : "auth-password-input-signin"}
                  type={showPw ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={isRegister ? "Min 8 characters" : "••••••••"}
                  className="h-12 rounded-none pr-11 bg-white/5 border-white/12 focus-visible:ring-[#E2E30A] focus-visible:border-[#E2E30A] text-white placeholder:text-zinc-500"
                />
                <button
                  type="button"
                  data-testid="auth-toggle-password-visibility"
                  onClick={() => setShowPw((v) => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 grid place-items-center text-zinc-500 hover:text-white transition-colors"
                  aria-label={showPw ? "Hide password" : "Show password"}
                >
                  {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              data-testid={isRegister ? "auth-submit-register" : "auth-submit-signin"}
              disabled={loading}
              className="w-full h-13 sm:h-14 rounded-none bg-[#E2E30A] hover:bg-white disabled:bg-white/10 disabled:text-white/40 text-black font-display font-black uppercase tracking-widest text-sm sm:text-base transition-colors group"
              style={{ boxShadow: loading ? "none" : "0 18px 40px -18px rgba(226,227,10,0.55)" }}
            >
              {loading ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <span className="inline-flex items-center gap-2">
                  {isRegister ? "Create account & continue" : "Sign in"}
                  <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </span>
              )}
            </Button>

            <p className="flex items-center justify-center gap-1.5 text-[10px] uppercase tracking-wider text-zinc-500 pt-1">
              <ShieldCheck className="h-3 w-3 text-[#E2E30A]" />
              Stripe secured · No VAT · Cancel anytime
            </p>
          </form>

          {/* Switch link at bottom */}
          <p className="text-center mt-8 text-sm text-zinc-400">
            {isRegister ? (
              <>Already a member?{" "}
                <button type="button" data-testid="auth-switch-to-signin" onClick={() => setMode("signin")} className="text-[#E2E30A] hover:underline font-semibold">
                  Sign in
                </button>
              </>
            ) : (
              <>New here?{" "}
                <button type="button" data-testid="auth-switch-to-register" onClick={goCreateAccount} className="text-[#E2E30A] hover:underline font-semibold">
                  Create an account
                </button>
              </>
            )}
          </p>
        </motion.div>
      </div>
    </div>
  );
}
